import React, { useState, useEffect, useRef } from 'react';
import LogoAnimation from '../components/LogoAnimation';
import Navbar from '../components/Navbar';
import HeroSection from '../components/HeroSection';
import StockWidget from '../components/StockWidget';
import SubsidiariesSection from '../components/SubsidiariesSection';
import MissionVisionSection from '../components/MissionVisionSection';
import AboutSection from '@/components/AboutSection';
import ContactSection from '../components/ContactSection';
import Footer from '../components/Footer';
import usePageMeta from '../hooks/usePageMeta';


/* ─── Responsive width hook ─────────────────────────────────── */
const useWindowWidth = () => {
  const [width, setWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1200
  );
  useEffect(() => {
    const handler = () => setWidth(window.innerWidth);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return width;
};

/* ─── Utility: scroll-triggered visibility ─────────────────── */
const useScrollReveal = (threshold = 0.12) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisible(true);
        else setVisible(false);
      },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
};

/* ─── Animated counter ───────────────────────────────────────── */
const Counter = ({ target, suffix = '', duration = 2000 }) => {
  const [ref, inView] = useScrollReveal(0.3);
  const [count, setCount] = useState(0);
  const animRef = useRef(null);

  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const num = parseFloat(target);
    const startTime = performance.now();
    const tick = (now) => {
      const progress = Math.min((now - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * num));
      if (progress < 1) animRef.current = requestAnimationFrame(tick);
      else setCount(num);
    };
    animRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(animRef.current);
  }, [inView, target, duration]);

  return <span ref={ref}>{count}{suffix}</span>;
};

/* ─── Reveal wrapper ─────────────────────────────────────────── */
const Reveal = ({ children, delay = 0, dir = 'up', className = '' }) => {
  const [ref, visible] = useScrollReveal(0.1);
  const t = dir === 'up' ? 'translateY(60px)' : dir === 'left' ? 'translateX(-60px)' : dir === 'right' ? 'translateX(60px)' : dir === 'scale' ? 'scale(0.85)' : 'translateY(60px)';
  return (
    <div ref={ref} className={className} style={{
      opacity: visible ? 1 : 0,
      transform: visible ? 'none' : t,
      transition: `opacity 0.85s cubic-bezier(0.16,1,0.3,1) ${delay}ms, transform 0.85s cubic-bezier(0.16,1,0.3,1) ${delay}ms`,
    }}>
      {children}
    </div>
  );
};


/* ─── Animated gold line ─────────────────────────────────────── */
const GoldLine = ({ width = 80 }) => {
  const [ref, visible] = useScrollReveal(0.1);
  return (
    <div ref={ref} style={{
      width: visible ? width : 0,
      height: 2,
      background: 'linear-gradient(to right, #c5963c, #f0c060, #c5963c)',
      borderRadius: 2,
      transition: 'width 1.2s cubic-bezier(0.16,1,0.3,1) 200ms',
    }} />
  );
};

/* ─── 3D CSS Cube ────────────────────────────────────────────── */
const Cube3D = () => {
  const [rot, setRot] = useState(0);
  useEffect(() => {
    let raf;
    const tick = () => { setRot(r => r + 0.25); raf = requestAnimationFrame(tick); };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);
  const size = 160;
  const half = size / 2;
  const faces = [
    { transform: `translateZ(${half}px)`, img: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=400&h=400&fit=crop&q=80' },
    { transform: `rotateY(90deg) translateZ(${half}px)`, img: 'https://images.unsplash.com/photo-1560520653-9e0e4c89eb11?w=400&h=400&fit=crop&q=80' },
    { transform: `rotateY(180deg) translateZ(${half}px)`, img: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=400&h=400&fit=crop&q=80' },
    { transform: `rotateY(-90deg) translateZ(${half}px)`, img: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=400&h=400&fit=crop&q=80' },
    { transform: `rotateX(90deg) translateZ(${half}px)`, img: 'https://images.unsplash.com/photo-1507679799987-c73779587ccf?w=400&h=400&fit=crop&q=80' },
    { transform: `rotateX(-90deg) translateZ(${half}px)`, img: 'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=400&h=400&fit=crop&q=80' },
  ];
  return (
    <div style={{ width: size, height: size, perspective: 600, margin: '0 auto' }}>
      <div style={{
        width: size, height: size,
        transformStyle: 'preserve-3d',
        transform: `rotateX(${rot * 0.4}deg) rotateY(${rot}deg)`,
        position: 'relative',
      }}>
        {faces.map((f, i) => (
          <div key={i} style={{
            position: 'absolute', width: size, height: size,
            transform: f.transform,
            overflow: 'hidden',
            border: '2px solid rgba(197,150,60,0.5)',
            borderRadius: 8,
          }}>
            <img src={f.img} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            <div style={{ position: 'absolute', inset: 0, background: 'rgba(54,1,83,0.15)' }} />
          </div>
        ))}
      </div>
    </div>
  );
};

/* ─── About Section (Light Theme) ───────────────────────────── */
const AboutSection2 = () => {
  const images = [
    { src: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=700&h=500&fit=crop&q=80', label: 'Real Estate', alt: 'Buildings' },
    { src: 'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=700&h=500&fit=crop&q=80', label: 'Capital Markets', alt: 'Charts' },
    { src: 'https://images.unsplash.com/photo-1507679799987-c73779587ccf?w=700&h=500&fit=crop&q=80', label: 'Leadership', alt: 'Executive' },
    { src: 'https://images.unsplash.com/photo-1560520653-9e0e4c89eb11?w=700&h=500&fit=crop&q=80', label: 'Global Trade', alt: 'Trade' },
    { src: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=700&h=500&fit=crop&q=80', label: 'Technology', alt: 'Tech' },
    { src: 'https://images.unsplash.com/photo-1512453979798-5ea266f8880c?w=700&h=500&fit=crop&q=80', label: 'UAE Presence', alt: 'Dubai' },
  ];

  return (
    <section id="about" style={{
      position: 'relative',
      background: 'linear-gradient(160deg, #faf8ff 0%, #fff9f0 40%, #f5f0ff 70%, #fffbf0 100%)',
      overflow: 'hidden',
    }}>
      {/* Decorative geometric shapes */}
      <div style={{ position: 'absolute', top: -120, right: -120, width: 500, height: 500, borderRadius: '50%', background: 'radial-gradient(circle, rgba(54,1,83,0.06) 0%, transparent 70%)', pointerEvents: 'none' }} />
      <div style={{ position: 'absolute', bottom: -80, left: -80, width: 400, height: 400, borderRadius: '50%', background: 'radial-gradient(circle, rgba(197,150,60,0.08) 0%, transparent 70%)', pointerEvents: 'none' }} />
      {/* Subtle grid */}
      <div style={{ position: 'absolute', inset: 0, opacity: 0.025, backgroundImage: 'linear-gradient(rgba(54,1,83,0.8) 1px, transparent 1px), linear-gradient(90deg, rgba(54,1,83,0.8) 1px, transparent 1px)', backgroundSize: '80px 80px', pointerEvents: 'none' }} />
      {/* Diagonal accent stripe */}
      <div style={{ position: 'absolute', top: 0, right: 0, width: 6, height: '100%', background: 'linear-gradient(to bottom, #360153, #c5963c, transparent)', opacity: 0.5 }} />

      {/* ── PART 1: IMPACT HEADER + CUBE ── */}
      <div style={{ maxWidth: 1280, margin: '0 auto', padding: '120px 40px 60px' }}>
        <Reveal>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 28 }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '7px 18px', borderRadius: 100, background: 'rgba(54,1,83,0.08)', border: '1px solid rgba(54,1,83,0.15)', color: '#360153', fontSize: 11, fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase' }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#c5963c', display: 'inline-block' }} />
              A Wealth of Experience
            </div>
            <div style={{ flex: 1, height: 1, background: 'linear-gradient(to right, rgba(54,1,83,0.15), transparent)' }} />
          </div>
        </Reveal>
        <div style={{ display: 'grid', gridTemplateColumns: '1.1fr 1fr', gap: 80, alignItems: 'flex-start' }}>
          {/* Left — Headline + cube below */}
          <div>
            <Reveal dir="left">
              <h2 style={{ fontSize: 'clamp(2.8rem, 4.5vw, 5rem)', fontWeight: 900, lineHeight: 1.0, letterSpacing: '-0.03em', color: '#0d0018', marginBottom: 48 }}>
                Shaping the<br />
                Future of{' '}
                <span style={{ position: 'relative', display: 'inline-block' }}>
                  <span style={{ background: 'linear-gradient(135deg, #360153 0%, #c5963c 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Investment</span>
                  <span style={{ position: 'absolute', bottom: -6, left: 0, right: 0, height: 4, background: 'linear-gradient(to right, #360153, #c5963c)', borderRadius: 2, opacity: 0.6 }} />
                </span>
              </h2>
            </Reveal>
            {/* 3D Cube sits here, below the headline */}
            <Reveal dir="left" delay={120}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 32 }}>
                <div>
                  <Cube3D />
                  <p style={{ textAlign: 'center', color: '#9060b0', fontSize: 10, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', marginTop: 10 }}>Our Sectors</p>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  {[
                    { value: '30+', label: 'Years of Legacy', sub: 'Since 1993', color: '#360153' },
                    { value: '50+', label: 'Investments Made', sub: 'Across sectors', color: '#c5963c' },
                    { value: '3', label: 'Global Subsidiaries', sub: 'India & UAE', color: '#34d399' },
                  ].map((s, i) => (
                    <div key={i} style={{
                      display: 'flex', alignItems: 'center', gap: 14,
                      padding: '12px 16px', borderRadius: 14,
                      background: '#fff',
                      boxShadow: '0 4px 20px rgba(54,1,83,0.08)',
                      border: `1px solid ${s.color}1a`,
                      transition: 'transform 0.25s, box-shadow 0.25s',
                      minWidth: 180,
                    }}
                      onMouseEnter={e => { e.currentTarget.style.transform = 'translateX(5px)'; e.currentTarget.style.boxShadow = `0 8px 32px ${s.color}18`; }}
                      onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 4px 20px rgba(54,1,83,0.08)'; }}>
                      <div style={{ width: 4, height: 36, borderRadius: 2, background: s.color, flexShrink: 0 }} />
                      <div>
                        <p style={{ color: s.color, fontWeight: 900, fontSize: 22, lineHeight: 1 }}>{s.value}</p>
                        <p style={{ color: '#1a0030', fontWeight: 700, fontSize: 12 }}>{s.label}</p>
                        <p style={{ color: '#9070b0', fontSize: 10 }}>{s.sub}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>
          </div>
          {/* Right — text */}
          <Reveal dir="right" delay={150}>
            <div style={{ paddingTop: 16 }}>
              <p style={{ color: '#3d2060', fontSize: 18, lineHeight: 1.8, marginBottom: 20, fontWeight: 400 }}>
                At Fundviser Capital, we are dedicated to transforming the investment landscape through innovation and strategic insight. Our extensive network and forward-thinking approach unlock exclusive opportunities, carefully structured for long-term success.
              </p>
              <p style={{ color: '#6b5080', fontSize: 16, lineHeight: 1.75, marginBottom: 40 }}>
                As leaders in the financial world, we continually reinvest in new ventures — strengthening our portfolio and building a legacy of excellence and lasting impact.
              </p>
              {/* 4 pillars inline in right column */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                {[
                  { num: '01', title: 'Strategic Insight', desc: 'Deep market intelligence honed over three decades — identifying asymmetric opportunities first.', color: '#360153' },
                  { num: '02', title: 'BSE-Listed Trust', desc: 'Full public accountability through BSE listing (530197) — transparent at every step.', color: '#c5963c' },
                  { num: '03', title: 'Risk Architecture', desc: 'Proprietary frameworks stress-test every investment across multiple economic scenarios.', color: '#34d399' },
                  { num: '04', title: 'Legacy Mindset', desc: 'Investing for generations, not quarters — our 30-year track record proves it.', color: '#a78bfa' },
                ].map((p, i) => (
                  <div key={i} style={{
                    padding: '18px 16px', borderRadius: 14,
                    background: '#fff', boxShadow: '0 4px 16px rgba(54,1,83,0.07)',
                    border: '1px solid rgba(54,1,83,0.06)', cursor: 'default',
                    transition: 'all 0.25s',
                  }}
                    onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = `0 14px 40px ${p.color}1e`; e.currentTarget.style.borderColor = `${p.color}28`; }}
                    onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(54,1,83,0.07)'; e.currentTarget.style.borderColor = 'rgba(54,1,83,0.06)'; }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                      <span style={{ fontSize: 10, fontWeight: 800, color: p.color, opacity: 0.45, letterSpacing: '0.1em' }}>{p.num}</span>
                      <div style={{ flex: 1, height: 1, background: `linear-gradient(to right, ${p.color}25, transparent)` }} />
                    </div>
                    <h4 style={{ color: '#0d0018', fontSize: 13, fontWeight: 800, marginBottom: 5 }}>{p.title}</h4>
                    <p style={{ color: '#6b5080', fontSize: 11, lineHeight: 1.55 }}>{p.desc}</p>
                    <div style={{ marginTop: 10, height: 3, borderRadius: 2, background: `linear-gradient(to right, ${p.color}, transparent)` }} />
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </div>

      {/* ── PART 2: IMAGE COLLAGE ── */}
      <div style={{ maxWidth: 1280, margin: '0 auto', padding: '0 40px 80px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 40, alignItems: 'center' }}>

          {/* Left — Multi-image collage */}
          <Reveal dir="left" delay={50}>
            <div style={{ position: 'relative', height: 560 }}>
              {/* Large hero image */}
              <div style={{
                position: 'absolute', top: 0, left: 0, width: '62%', height: '58%',
                borderRadius: 20, overflow: 'hidden',
                boxShadow: '0 20px 60px rgba(54,1,83,0.18)',
                transform: 'rotate(-1.5deg)',
                zIndex: 3,
                transition: 'transform 0.4s ease, box-shadow 0.4s ease',
              }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'rotate(0deg) scale(1.03)'; e.currentTarget.style.boxShadow = '0 30px 80px rgba(54,1,83,0.28)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'rotate(-1.5deg)'; e.currentTarget.style.boxShadow = '0 20px 60px rgba(54,1,83,0.18)'; }}>
                <img src={images[0].src} alt={images[0].alt} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '20px 16px 14px', background: 'linear-gradient(to top, rgba(54,1,83,0.85), transparent)' }}>
                  <span style={{ color: '#f0c060', fontSize: 11, fontWeight: 700, letterSpacing: '0.15em', textTransform: 'uppercase' }}>{images[0].label}</span>
                </div>
              </div>

              {/* Top right image */}
              <div style={{
                position: 'absolute', top: 0, right: 0, width: '35%', height: '38%',
                borderRadius: 16, overflow: 'hidden',
                boxShadow: '0 12px 40px rgba(197,150,60,0.2)',
                transform: 'rotate(2deg)',
                zIndex: 4,
                transition: 'transform 0.4s ease',
              }}
                onMouseEnter={e => e.currentTarget.style.transform = 'rotate(0deg) scale(1.05)'}
                onMouseLeave={e => e.currentTarget.style.transform = 'rotate(2deg)'}>
                <img src={images[2].src} alt={images[2].alt} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                <div style={{ position: 'absolute', inset: 0, background: 'rgba(54,1,83,0.1)' }} />
                <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '14px 12px 10px', background: 'linear-gradient(to top, rgba(54,1,83,0.85), transparent)' }}>
                  <span style={{ color: '#f0c060', fontSize: 10, fontWeight: 700, letterSpacing: '0.15em', textTransform: 'uppercase' }}>{images[2].label}</span>
                </div>
              </div>

              {/* Middle right image */}
              <div style={{
                position: 'absolute', top: '35%', right: '2%', width: '38%', height: '34%',
                borderRadius: 16, overflow: 'hidden',
                boxShadow: '0 16px 50px rgba(54,1,83,0.2)',
                transform: 'rotate(-1deg)',
                zIndex: 3,
                transition: 'transform 0.4s ease',
              }}
                onMouseEnter={e => e.currentTarget.style.transform = 'rotate(0deg) scale(1.04)'}
                onMouseLeave={e => e.currentTarget.style.transform = 'rotate(-1deg)'}>
                <img src={images[4].src} alt={images[4].alt} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '14px 12px 10px', background: 'linear-gradient(to top, rgba(54,1,83,0.85), transparent)' }}>
                  <span style={{ color: '#f0c060', fontSize: 10, fontWeight: 700, letterSpacing: '0.15em', textTransform: 'uppercase' }}>{images[4].label}</span>
                </div>
              </div>

              {/* Bottom left image */}
              <div style={{
                position: 'absolute', bottom: 0, left: '5%', width: '55%', height: '38%',
                borderRadius: 16, overflow: 'hidden',
                boxShadow: '0 16px 50px rgba(197,150,60,0.15)',
                transform: 'rotate(1deg)',
                zIndex: 4,
                transition: 'transform 0.4s ease',
              }}
                onMouseEnter={e => e.currentTarget.style.transform = 'rotate(0deg) scale(1.03)'}
                onMouseLeave={e => e.currentTarget.style.transform = 'rotate(1deg)'}>
                <img src={images[5].src} alt={images[5].alt} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '14px 12px 10px', background: 'linear-gradient(to top, rgba(54,1,83,0.85), transparent)' }}>
                  <span style={{ color: '#f0c060', fontSize: 10, fontWeight: 700, letterSpacing: '0.15em', textTransform: 'uppercase' }}>{images[5].label}</span>
                </div>
              </div>

              {/* Floating stat badge */}
              <div style={{
                position: 'absolute', top: '54%', left: '38%',
                padding: '14px 20px', borderRadius: 16, zIndex: 10,
                background: 'rgba(255,255,255,0.95)',
                boxShadow: '0 20px 60px rgba(54,1,83,0.18)',
                border: '1px solid rgba(54,1,83,0.08)',
                backdropFilter: 'blur(10px)',
              }}>
                <p style={{ color: '#c5963c', fontWeight: 900, fontSize: 26, lineHeight: 1 }}>30+</p>
                <p style={{ color: '#0d0018', fontWeight: 700, fontSize: 12 }}>Years of Excellence</p>
                <GoldLine width={60} />
              </div>
            </div>
          </Reveal>

          {/* Right — rich copy */}
          <Reveal dir="right" delay={100}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 32 }}>
                <div style={{ height: 1, width: 48, background: 'linear-gradient(to right, #360153, #c5963c)' }} />
                <span style={{ color: '#360153', fontSize: 11, fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase' }}>Our Portfolio</span>
              </div>
              <h3 style={{ fontSize: 'clamp(2rem, 3vw, 3rem)', fontWeight: 900, color: '#0d0018', lineHeight: 1.1, marginBottom: 24, letterSpacing: '-0.02em' }}>
                A Diversified<br />
                <span style={{ background: 'linear-gradient(135deg, #360153, #c5963c)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Global Portfolio</span>
              </h3>
              <p style={{ color: '#5a3880', fontSize: 16, lineHeight: 1.8, marginBottom: 32 }}>
                From high-growth real estate ventures to dynamic capital markets, entertainment technology, and international trade — our diversified portfolio spans the full spectrum of opportunity.
              </p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginBottom: 36 }}>
                {[
                  { label: 'Real Estate & Infrastructure', pct: 35, color: '#360153' },
                  { label: 'Capital Markets & Equity', pct: 28, color: '#c5963c' },
                  { label: 'Global Trade & Commodities', pct: 22, color: '#34d399' },
                  { label: 'Entertainment & Technology', pct: 15, color: '#a78bfa' },
                ].map((item, i) => (
                  <div key={i}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                      <span style={{ color: '#1a0030', fontSize: 13, fontWeight: 600 }}>{item.label}</span>
                      <span style={{ color: item.color, fontSize: 13, fontWeight: 800 }}>{item.pct}%</span>
                    </div>
                    <div style={{ height: 6, background: 'rgba(54,1,83,0.06)', borderRadius: 3, overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${item.pct}%`, background: `linear-gradient(to right, ${item.color}, ${item.color}88)`, borderRadius: 3 }} />
                    </div>
                  </div>
                ))}
              </div>

              <div style={{ display: 'flex', gap: 16 }}>
                <a href="/investment-sectors" style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  padding: '14px 28px', borderRadius: 12,
                  background: 'linear-gradient(135deg, #360153, #5a0280)',
                  color: '#fff', fontWeight: 700, fontSize: 14, textDecoration: 'none',
                  boxShadow: '0 8px 28px rgba(54,1,83,0.25)',
                  transition: 'all 0.25s',
                }}
                  onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 14px 40px rgba(54,1,83,0.35)'; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 8px 28px rgba(54,1,83,0.25)'; }}>
                  Explore Sectors →
                </a>
                <a href="/our-subsidiaries" style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  padding: '14px 28px', borderRadius: 12,
                  background: 'transparent',
                  border: '1.5px solid rgba(54,1,83,0.2)',
                  color: '#360153', fontWeight: 700, fontSize: 14, textDecoration: 'none',
                  transition: 'all 0.25s',
                }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'rgba(54,1,83,0.06)'; e.currentTarget.style.borderColor = 'rgba(54,1,83,0.4)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = 'rgba(54,1,83,0.2)'; }}>
                  Our Subsidiaries
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};


/* ─── Stats data ─────────────────────────────────────────────── */
const stats = [
  { value: 30, suffix: '+', label: 'Years of Legacy', sub: 'Since 1993' },
  { value: 50, suffix: '+', label: 'Investments Made', sub: 'Across sectors' },
  { value: 98, suffix: '%', label: 'Client Satisfaction', sub: 'Stakeholder trust' },
  { value: 100, suffix: 'Cr+', label: 'Portfolio Value', sub: 'Assets under mgmt.' },
];

/* ─── Sectors data ───────────────────────────────────────────── */
const sectors = [
  {
    num: '01', title: 'Real Estate', subtitle: 'Commercial & Residential',
    desc: 'Commercial complexes, residential developments, and undervalued assets poised for significant appreciation across India. From SRA redevelopment to premium commercial leasing.',
    tags: ['SRA Projects', 'Commercial Leasing', 'Redevelopment', 'Land Banking'],
    accent: '#a78bfa',
    img: '/Main_header_building.png',
    link: '/investment-sectors#real-estate',
  },
  {
    num: '02', title: 'Bullion', subtitle: 'Gold & Silver',
    desc: 'A structural allocation to precious metals gold and silver held as a disciplined hedge against inflation, currency depreciation, and correlated market volatility. Stability with genuine uncorrelated returns.',
    tags: ['Gold', 'Silver', 'Inflation Hedge', 'Portfolio Diversification'],
    accent: '#fbbf24',
    img: 'https://images.unsplash.com/photo-1610375461246-83df859d849d?w=1400&h=900&fit=crop&q=85',
    link: '/investment-sectors#bullion',
  },
  {
    num: '03', title: 'Equities', subtitle: 'Listed & Unlisted Markets',
    desc: 'Strategic capital deployed across high-growth equity markets with decades of expertise. BSE-listed credibility with deep market intelligence since 1993, covering listed equities, derivatives, and commodities.',
    tags: ['BSE: 530197', 'Listed Equities', 'Derivatives', 'Commodities'],
    accent: '#f59e0b',
    img: 'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=1400&h=900&fit=crop&q=85',
    link: '/investment-sectors#equities',
  },
  {
    num: '04', title: 'Startup & VC', subtitle: 'Early-Stage Investments',
    desc: 'Backing India\'s next generation of disruptive founders with capital, mentorship, and strategic networks. We invest at the intersection of vision, execution, and market timing.',
    tags: ['Seed Funding', 'Series A', 'Mentorship', 'Portfolio Support'],
    accent: '#34d399',
    img: 'z_16.png',
    link: '/investment-sectors#startup-vc',
  },
];

/* ─── Sector Row — alternating image / content ───────────────── */
const SectorRow = ({ sector, reversed }) => {
  const [rowRef, rowVisible] = useScrollReveal(0.15);
  const [hovered, setHovered] = useState(false);
  const winW = useWindowWidth();
  const isMobile = winW < 768;
  const isTablet = winW >= 768 && winW < 1024;

  // On mobile: always column (image top, content bottom), no reversal
  const flexDir = isMobile ? 'column' : (reversed ? 'row-reverse' : 'row');
  const imgFlex = isMobile ? 'none' : (isTablet ? '0 0 44%' : '0 0 46%');
  const imgH    = isMobile ? 240 : (isTablet ? 360 : '100%');
  const contentPad = isMobile ? '24px 20px' : (isTablet ? '28px 32px' : '36px 48px');

  // On mobile/tablet slide-in is vertical instead of horizontal
  const hiddenTransform = isMobile
    ? 'translateY(40px)'
    : (reversed ? 'translateX(70px)' : 'translateX(-70px)');
  const hiddenTransformContent = isMobile
    ? 'translateY(40px)'
    : (reversed ? 'translateX(-70px)' : 'translateX(70px)');

  return (
    <div
      ref={rowRef}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => { window.location.href = sector.link; }}
      style={{
        display: 'flex',
        flexDirection: flexDir,
        minHeight: isMobile ? 'auto' : 380,
        borderTop: '1px solid rgba(255,255,255,0.04)',
        cursor: 'pointer',
      }}
    >
      {/* ── Image half ── */}
      <div style={{
        flex: imgFlex,
        height: imgH,
        position: 'relative', overflow: 'hidden',
        opacity: rowVisible ? 1 : 0,
        transform: rowVisible ? 'none' : hiddenTransform,
        transition: 'opacity 1s cubic-bezier(0.16,1,0.3,1), transform 1s cubic-bezier(0.16,1,0.3,1)',
      }}>
        <img
          src={sector.img}
          alt={sector.title}
          style={{
            width: '100%', height: '100%', objectFit: 'cover',
            transform: hovered ? 'scale(1.09)' : 'scale(1.02)',
            transition: 'transform 1.1s cubic-bezier(0.4,0,0.2,1)',
          }}
        />
        {/* Directional vignette — on mobile use bottom-fade */}
        <div style={{
          position: 'absolute', inset: 0,
          background: isMobile
            ? 'linear-gradient(to bottom, transparent 40%, rgba(6,0,15,0.7) 100%)'
            : (reversed
              ? 'linear-gradient(to left, rgba(6,0,15,0.65) 0%, rgba(6,0,15,0.1) 45%, transparent 100%)'
              : 'linear-gradient(to right, rgba(6,0,15,0.65) 0%, rgba(6,0,15,0.1) 45%, transparent 100%)'),
        }} />
        {/* Colour tint */}
        <div style={{
          position: 'absolute', inset: 0,
          background: `linear-gradient(135deg, ${sector.accent}16 0%, transparent 65%)`,
          opacity: hovered ? 1 : 0.55, transition: 'opacity 0.55s ease',
        }} />
        {/* Ghost number — hide on mobile to avoid clutter */}
        {!isMobile && (
          <div style={{
            position: 'absolute', bottom: 12,
            ...(reversed ? { right: 28 } : { left: 28 }),
            fontSize: 140, fontWeight: 900, lineHeight: 1,
            color: 'rgba(255,255,255,0.05)',
            letterSpacing: '-0.06em', userSelect: 'none', pointerEvents: 'none',
          }}>
            {sector.num}
          </div>
        )}
        {/* Subtitle pill */}
        <div style={{
          position: 'absolute', top: 16, left: 16,
          padding: '5px 13px', borderRadius: 100,
          background: `${sector.accent}22`, border: `1px solid ${sector.accent}38`,
          backdropFilter: 'blur(12px)', color: sector.accent,
          fontSize: 10, fontWeight: 700, letterSpacing: '0.13em', textTransform: 'uppercase',
          opacity: hovered ? 1 : 0.7, transition: 'opacity 0.4s ease',
        }}>
          {sector.subtitle}
        </div>
      </div>

      {/* ── Content half ── */}
      <div style={{
        flex: isMobile ? 'none' : 1,
        display: 'flex', flexDirection: 'column', justifyContent: 'center',
        padding: contentPad,
        background: hovered ? 'rgba(18,4,30,1)' : '#06000f',
        borderLeft:  (!isMobile && !reversed) ? '1px solid rgba(255,255,255,0.03)' : 'none',
        borderRight: (!isMobile && reversed)  ? '1px solid rgba(255,255,255,0.03)' : 'none',
        opacity: rowVisible ? 1 : 0,
        transform: rowVisible ? 'none' : hiddenTransformContent,
        transition: [
          'opacity 1s cubic-bezier(0.16,1,0.3,1) 0.18s',
          'transform 1s cubic-bezier(0.16,1,0.3,1) 0.18s',
          'background 0.4s ease',
        ].join(', '),
      }}>
        <div style={{
          fontSize: 11, fontWeight: 800, letterSpacing: '0.22em',
          textTransform: 'uppercase', color: sector.accent, marginBottom: 14, opacity: 0.85,
        }}>
          {sector.num} / 0{sectors.length}
        </div>
        <h3 style={{
          fontSize: isMobile ? '1.75rem' : 'clamp(1.8rem, 2.4vw, 3rem)',
          fontWeight: 900, color: '#fff',
          lineHeight: 1.05, letterSpacing: '-0.03em', marginBottom: 12,
        }}>
          {sector.title}
        </h3>
        {/* Animated accent bar */}
        <div style={{
          height: 3, borderRadius: 2, marginBottom: 20,
          background: `linear-gradient(to right, ${sector.accent}, ${sector.accent}35)`,
          width: hovered ? 72 : 36,
          transition: 'width 0.55s cubic-bezier(0.4,0,0.2,1)',
        }} />
        <p style={{
          color: hovered ? 'rgba(255,255,255,0.72)' : 'rgba(255,255,255,0.48)',
          fontSize: isMobile ? 14 : 15.5, lineHeight: 1.82,
          marginBottom: 28, transition: 'color 0.4s ease',
        }}>
          {sector.desc}
        </p>
        {/* Tags */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 32 }}>
          {sector.tags.map((tag, j) => (
            <span key={j} style={{
              padding: '5px 13px', borderRadius: 100,
              background: `${sector.accent}14`, border: `1px solid ${sector.accent}28`,
              color: sector.accent, fontSize: 11, fontWeight: 700, letterSpacing: '0.07em',
            }}>
              {tag}
            </span>
          ))}
        </div>
        {/* CTA */}
        <a href={sector.link} style={{
          display: 'inline-flex', alignItems: 'center',
          gap: hovered ? 14 : 8,
          color: '#fff', fontWeight: 700, fontSize: 14,
          textDecoration: 'none', letterSpacing: '0.02em',
          transition: 'gap 0.3s ease',
        }}>
          <span>Explore {sector.title}</span>
          <span style={{
            width: 34, height: 34, borderRadius: '50%', flexShrink: 0,
            background: `linear-gradient(135deg, ${sector.accent}, ${sector.accent}70)`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 16, color: '#fff',
            transform: hovered ? 'translateX(4px)' : 'none',
            transition: 'transform 0.3s ease',
          }}>→</span>
        </a>
      </div>
    </div>
  );
};

/* ─── Main Page ─────────────────────────────────────────────── */
const Home = () => {
  usePageMeta();
  const [showLogoAnimation, setShowLogoAnimation] = useState(true);
  const [contentVisible, setContentVisible] = useState(false);
  const homeW = useWindowWidth();
  const sectorsHeaderPad = homeW < 768 ? '80px 20px 56px' : homeW < 1024 ? '100px 32px 72px' : '130px 40px 90px';
  const sectorsCTAPad    = homeW < 768 ? '48px 20px 72px' : '80px 40px 120px';

  useEffect(() => {
    const hasSeenAnimation = sessionStorage.getItem('hasSeenLogoAnimation');

    if (hasSeenAnimation) {
      setShowLogoAnimation(false);
      setContentVisible(true);
    } else {
      const timer = setTimeout(() => {
        setContentVisible(true);
        sessionStorage.setItem('hasSeenLogoAnimation', 'true');
      }, 2500);

      return () => clearTimeout(timer);
    }
  }, []);

  return (
    <div className="min-h-screen">
      {showLogoAnimation && (
        <LogoAnimation onComplete={() => setShowLogoAnimation(false)} />
      )}

      <div className={`transition-opacity duration-500 ${contentVisible ? 'opacity-100' : 'opacity-0'}`}>
        <Navbar />
        <HeroSection />
        <StockWidget />

        {/* ══ ABOUT ══════════════════════════════════════════════ */}
        <AboutSection />

        {/* ══ INVESTMENT SECTORS ════════════════════════════════ */}
        <section id="sectors" style={{ position: 'relative', background: '#06000f', overflow: 'hidden' }}>

          {/* Ambient glow blobs */}
          <div style={{ position: 'absolute', top: '15%', left: '-8%', width: 700, height: 700, borderRadius: '50%', background: 'radial-gradient(circle, rgba(54,1,83,0.18) 0%, transparent 70%)', pointerEvents: 'none' }} />
          <div style={{ position: 'absolute', bottom: '15%', right: '-8%', width: 600, height: 600, borderRadius: '50%', background: 'radial-gradient(circle, rgba(197,150,60,0.12) 0%, transparent 70%)', pointerEvents: 'none' }} />

          {/* ── Section header ── */}
          <div style={{ maxWidth: 900, margin: '0 auto', padding: sectorsHeaderPad, textAlign: 'center', position: 'relative', zIndex: 2 }}>
            <Reveal>
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                padding: '7px 18px', borderRadius: 100,
                background: 'rgba(197,150,60,0.1)', border: '1px solid rgba(197,150,60,0.28)',
                color: '#c5963c', fontSize: 11, fontWeight: 700,
                letterSpacing: '0.18em', textTransform: 'uppercase', marginBottom: 24,
              }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#c5963c', display: 'inline-block' }} />
                Investment Universe
              </div>
              <h2 style={{
                fontSize: 'clamp(2.1rem, 5vw, 5.5rem)', fontWeight: 900,
                lineHeight: 1.0, letterSpacing: '-0.03em', color: '#fff', marginBottom: 24,
              }}>
                Sectors We{' '}
                <span style={{ background: 'linear-gradient(135deg, #a78bfa, #c5963c)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                  Dominate
                </span>
              </h2>
              <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: 'clamp(14px, 2vw, 18px)', lineHeight: 1.72, maxWidth: 580, margin: '0 auto' }}>
                A multi-sector portfolio engineered for resilience each vertical chosen for long-term compounding potential and strategic alignment with India's growth story.
              </p>
            </Reveal>
          </div>

          {/* ── Alternating sector rows ── */}
          <div style={{ position: 'relative', zIndex: 2 }}>
            {sectors.map((s, i) => (
              <SectorRow key={i} sector={s} reversed={i % 2 !== 0} />
            ))}
          </div>

          {/* ── Bottom CTA ── */}
          <div style={{ textAlign: 'center', padding: sectorsCTAPad, position: 'relative', zIndex: 2 }}>
            <Reveal>
              <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: 12, fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', marginBottom: 24 }}>
                Explore our complete investment portfolio
              </p>
              <a href="/investment-sectors" style={{
                display: 'inline-flex', alignItems: 'center', gap: 10,
                padding: homeW < 768 ? '13px 24px' : '16px 40px', borderRadius: 14,
                background: 'linear-gradient(135deg, #360153, #c5963c)',
                color: '#fff', fontWeight: 700, fontSize: 16, textDecoration: 'none',
                boxShadow: '0 8px 40px rgba(54,1,83,0.45)',
                transition: 'transform 0.25s, box-shadow 0.25s',
              }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 18px 56px rgba(54,1,83,0.6)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 8px 40px rgba(54,1,83,0.45)'; }}
              >
                View All Investment Sectors →
              </a>
            </Reveal>
          </div>
        </section>

        <SubsidiariesSection />
        <MissionVisionSection />
        <ContactSection />
        <Footer />
      </div>
    </div>
  );
};

export default Home;
